import { FC, ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext, useParams } from "react-router-dom";

import { ClientOutletContext } from "@/routes/ClientRoute";
import { BreadcrumbType } from "@/types/breadcrumb-types";
import type { RouteConfigurations } from "@/types/route-types";

// Route wrapper component that handles breadcrumb configuration
export const RouteWrapper: FC<{
  children: ReactNode;
  Wrapper?: React.ComponentType<{
    children: React.ReactNode;
    breadcrumb?: BreadcrumbType;
    title?: string;
    subHeader?: string;
    actionButton?: ReactNode;
    excludeInnerLayout?: boolean;
  }>;
  routeConfigurations?: RouteConfigurations;
}> = ({ children, Wrapper, routeConfigurations }) => {
  const params = useParams();
  const { t } = useTranslation();
  const outletContext = useOutletContext();
  
  const isClientContext = (ctx: unknown): ctx is ClientOutletContext => {
    return Boolean(
      ctx && 
      typeof ctx === 'object' && 
      'setTitle' in ctx && 
      'setBreadcrumb' in ctx && 
      'setExcludeInnerLayout' in ctx &&
      typeof (ctx as ClientOutletContext).setTitle === 'function' && 
      typeof (ctx as ClientOutletContext).setBreadcrumb === 'function' && 
      typeof (ctx as ClientOutletContext).setExcludeInnerLayout === 'function'
    );
  };
  
  const clientContext = isClientContext(outletContext) ? outletContext : undefined;

  useEffect(() => {
    if (clientContext) {
      const excludeInnerLayout = routeConfigurations?.excludeInnerLayout !== undefined
        ? (typeof routeConfigurations.excludeInnerLayout === "function"
          ? routeConfigurations.excludeInnerLayout({ params, t })
          : routeConfigurations.excludeInnerLayout)
        : false;
      
      clientContext.setExcludeInnerLayout(excludeInnerLayout);

      if (routeConfigurations) {
        const title =
          typeof routeConfigurations.title === "function"
            ? routeConfigurations.title({ params, t })
            : routeConfigurations.title;

        const breadcrumb =
          typeof routeConfigurations.breadcrumb === "function"
            ? routeConfigurations.breadcrumb({ params, t })
            : routeConfigurations.breadcrumb;

        clientContext.setTitle(title);
        clientContext.setBreadcrumb(breadcrumb);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeConfigurations, params, t]);

  if (clientContext && routeConfigurations) {
    return children;
  }

  if (!Wrapper) {
    return <>{children}</>;
  }

  if (!routeConfigurations) {
    return <Wrapper>{children}</Wrapper>;
  }

  const title =
    typeof routeConfigurations.title === "function"
      ? routeConfigurations.title({ params, t })
      : routeConfigurations.title;

  const breadcrumb =
    typeof routeConfigurations.breadcrumb === "function"
      ? routeConfigurations.breadcrumb({ params, t })
      : routeConfigurations.breadcrumb;

  const excludeInnerLayout = routeConfigurations.excludeInnerLayout !== undefined
    ? (typeof routeConfigurations.excludeInnerLayout === "function"
      ? routeConfigurations.excludeInnerLayout({ params, t })
      : routeConfigurations.excludeInnerLayout)
    : undefined;

  const subHeader =
    typeof routeConfigurations.subHeader === "function"
      ? routeConfigurations.subHeader({ params, t })
      : routeConfigurations.subHeader;

  return (
    <Wrapper
      breadcrumb={breadcrumb}
      title={title}
      subHeader={subHeader}
      actionButton={routeConfigurations.actionButton}
      excludeInnerLayout={excludeInnerLayout}
    >
      {children}
    </Wrapper>
  );
};
